import React from 'react';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import Field from 'redux-form/es/Field';
import InputSelectField from 'components/Credit24/Core/Controls/InputSelectField';
import InputField from 'components/Credit24/Core/Controls/InputField';
import DatePicker from 'components/Credit24/Core/Controls/DatePicker';
import InputToggleGroup from 'components/Credit24/Core/Controls/InputToggleGroup';
import { MEDICARE_CARD_COLOR_GREEN } from 'consts';
import { useApplicationFlags } from 'hooks/useApplicationFlags';
import { useTranslationSuffix } from 'hooks/useTranslationSuffix';
import {
  drivingLicenceHintKeys,
  drivingLicenceRules,
} from '../../../../consts/loan/AU/identDocument';

const IdentDocumentsAU = ({
  identDocumentTypes,
  states,
  medicareReferenceNumbers,
  medicalCardType,
  countryOfIssue,
  drivingLicenceMinimumValidDate,
  drivingLicenceMaximumValidDate,
  medicareMinimumValidDate,
  medicareMaximumValidDate,
  passportMinimumValidDate,
  passportMaximumValidDate,
  selectedDocumentType,
  selectedCardColour,
  identFormError,
  removeActionError,
  selectedDrivingLicenceState,
}) => {
  const { isOcmOrUpgrade } = useApplicationFlags();

  if (selectedDocumentType !== 'drivingLicence' && identFormError) {
    removeActionError();
  }

  const showFullDataPicker = !INCREASE_APP && selectedCardColour;
  const isGreenCardSelected = selectedCardColour === MEDICARE_CARD_COLOR_GREEN;

  const translationKeys = {
    title: 'ident_documents.title',
    subtitle: 'ident_documents.subtitle',
    medicareDesc: 'ident_document.medicare.desc',
  };

  const translations = useTranslationSuffix('upgrade', translationKeys);
  const subtitleStyles = isOcmOrUpgrade ? 'ident-document__subtitle-upgrade' : 'ident-document__subtitle';
  const drivingLicenceHintKey = drivingLicenceHintKeys[selectedDrivingLicenceState]
    || 'ident_document.driving_licence.hint';
  const selectedLicenceRule = drivingLicenceRules[selectedDrivingLicenceState];
  const drivingLicenceMaxCharacters = selectedLicenceRule?.maxCharacters ?? 10;


  return (
    <div className="loan-form__step">
      <h1 className="loan-form__step-title">
        <FormattedMessage id={translations.title} />
      </h1>
      <div className="loan-form__step-body">
        <div className="grid grid--middle">
          <div className="grid__item seven-twelfths palm-one-whole">
            <h2 className={subtitleStyles}>
              <FormattedHTMLMessage id={translations.subtitle} />
            </h2>
          </div>
          <div className="grid__item five-twelfths palm-one-whole">
            <InputToggleGroup
              name="identDocumentType"
              list={identDocumentTypes}
            />
          </div>

        </div>
        {selectedDocumentType === 'drivingLicence' && (
          <>
            {identFormError && (
              <p className="txt-error">
                <FormattedMessage id="ident_document.driving_licence.error" />
              </p>
            )}
            <div className="grid">
              <div className="grid__item one-half palm-one-whole">
                <Field
                  name="drivingLicenceState"
                  component={InputSelectField}
                  options={states}
                  emptyTextKey="ident_document.driving_licence.state"
                  isFullWidth
                />
              </div>
              <div className="grid__item one-half palm-one-whole">
                <Field
                  title={<FormattedMessage id="ident_document.driving_licence.number" />}
                  hintConfig={{ titleKey: drivingLicenceHintKey }}
                  name="drivingLicenceNumber"
                  component={InputField}
                  isFullWidth
                  maxCharacters={drivingLicenceMaxCharacters}
                  allowOnlyAlphanumeric
                />
              </div>
              <div className="grid__item one-whole palm-one-whole">
                <DatePicker
                  titleKey="ident_document.driving_licence.expiry_date"
                  minDate={drivingLicenceMinimumValidDate}
                  maxDate={drivingLicenceMaximumValidDate}
                  fieldNameDay="drivingLicenceExpirationDay"
                  fieldNameMonth="drivingLicenceExpirationMonth"
                  fieldNameYear="drivingLicenceExpirationYear"
                  isMonthNumeric
                />
              </div>
            </div>
          </>
        )}
        {selectedDocumentType === 'medicare' && (
          <div className="grid">
            <div className="grid__item one-half palm-one-whole">
              <Field
                title={<FormattedMessage id="ident_document.medicare.number" />}
                name="medicareNumber"
                component={InputField}
                isMaskedInput
                mask="9999999999"
                maskChar="_"
                isErrorVisible
                isFullWidth
              />
            </div>
            <div className="grid__item one-half palm-one-whole">
              <Field
                name="medicareReferenceNumber"
                component={InputSelectField}
                options={medicareReferenceNumbers}
                emptyTextKey="ident_document.medicare.reference_number"
                isFullWidth
              />
              <p className="hint">
                <FormattedMessage id={translations.medicareDesc} />
              </p>
            </div>
            {isOcmOrUpgrade
              ? (
                <div className="grid__item one-whole palm-one-whole">
                  <DatePicker
                    titleKey="ident_document.medicare.expiration"
                    minDate={medicareMinimumValidDate}
                    maxDate={medicareMaximumValidDate}
                    fieldNameMonth="medicareExpirationMonth"
                    fieldNameYear="medicareExpirationYear"
                    isDayHidden
                    isMonthNumeric
                  />
                </div>
              ) : (
                <>
                  <div className="grid__item one-half palm-one-whole">
                    <Field
                      name="cardColour"
                      component={InputSelectField}
                      options={medicalCardType}
                      emptyTextKey="medicare.card_color.empty_text_key"
                      isFullWidth
                    />
                  </div>
                  {showFullDataPicker && (
                  <div className="grid__item one-whole palm-one-whole">
                    {isGreenCardSelected ? (
                      <DatePicker
                        titleKey="ident_document.medicare.expiration"
                        minDate={medicareMinimumValidDate}
                        maxDate={medicareMaximumValidDate}
                        fieldNameMonth="medicareExpirationMonth"
                        fieldNameYear="medicareExpirationYear"
                        isDayHidden
                        isMonthNumeric
                      />
                    ) : (
                      <DatePicker
                        titleKey="ident_document.medicare.expiration"
                        minDate={medicareMinimumValidDate}
                        maxDate={medicareMaximumValidDate}
                        fieldNameDay="medicareExpirationDay"
                        fieldNameMonth="medicareExpirationMonth"
                        fieldNameYear="medicareExpirationYear"
                        isMonthNumeric
                      />
                    )}
                  </div>
                  )}
                </>
              )}
          </div>
        )}
        {selectedDocumentType === 'passport' && (
          isOcmOrUpgrade ? (
            <Field
              title={<FormattedMessage id="ident_document.passport.number" />}
              hintConfig={{ titleKey: 'ident_document.passport.hint' }}
              name="passportNumber"
              component={InputField}
              isFullWidth
            />
          ) : (
            <>
              <div className="grid">
                <div className="grid__item one-half palm-one-whole">
                  <Field
                    title={<FormattedMessage id="ident_document.passport.number" />}
                    hintConfig={{ titleKey: 'ident_document.passport.hint' }}
                    name="passportNumber"
                    component={InputField}
                    isFullWidth
                    maxCharacters={10}
                    allowOnlyAlphanumeric
                  />
                </div>
                <div className="grid__item one-half palm-one-whole">
                  <Field
                    title={<FormattedMessage id="ident_document.passport.country_of_issue" />}
                    name="countryOfIssue"
                    component={InputSelectField}
                    options={countryOfIssue}
                    emptyTextKey="ident_document.passport.country_of_issue.empty_text_key"
                    isFullWidth
                  />
                </div>
              </div>
              <div className="grid">
                <div className="grid__item one-whole palm-one-whole">
                  <DatePicker
                    titleKey="ident_document.passport.expiration_date"
                    minDate={passportMinimumValidDate}
                    maxDate={passportMaximumValidDate}
                    fieldNameDay="passportExpirationDay"
                    fieldNameMonth="passportExpirationMonth"
                    fieldNameYear="passportExpirationYear"
                    isMonthNumeric
                  />
                </div>
              </div>
            </>
          ))}
      </div>
    </div>
  );
};

IdentDocumentsAU.propTypes = {
  identDocumentTypes: PropTypes.array.isRequired,
  states: PropTypes.array.isRequired,
  medicareReferenceNumbers: PropTypes.array.isRequired,
  medicalCardType: PropTypes.array.isRequired,
  countryOfIssue: PropTypes.array.isRequired,
  drivingLicenceMinimumValidDate: PropTypes.object.isRequired,
  drivingLicenceMaximumValidDate: PropTypes.object.isRequired,
  medicareMinimumValidDate: PropTypes.object.isRequired,
  medicareMaximumValidDate: PropTypes.object.isRequired,
  passportMinimumValidDate: PropTypes.object.isRequired,
  passportMaximumValidDate: PropTypes.object.isRequired,
  selectedDocumentType: PropTypes.string,
  selectedCardColour: PropTypes.string,
  identFormError: PropTypes.bool,
  removeActionError: PropTypes.func,
  selectedDrivingLicenceState: PropTypes.string,
};

export default IdentDocumentsAU;
